import { useEffect, useState } from 'react';
import { makeStyles, tokens, Text, Spinner, Badge, Button } from '@fluentui/react-components';
import { apiGet } from '../api/client';
import type { PipelineTimeline as Timeline, PipelineEvent } from '../api/types';

// Vertical event list rendered from GET /api/pipeline/{runType}/{runId}.
// Lazy-loads when mounted; call with a stable key on the parent so switching
// runs re-fetches cleanly.

interface Props {
  runType: 'chat' | 'benchmark' | 'ingestion';
  runId: string;
}

const useStyles = makeStyles({
  wrap: {
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
    paddingLeft: '16px',
    marginLeft: '8px',
  },
  row: {
    position: 'relative',
    marginBottom: '14px',
  },
  dot: {
    position: 'absolute',
    left: '-24px',
    top: '4px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: tokens.colorBrandBackground,
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground1}`,
  },
  dotFailed: { background: tokens.colorPaletteRedBackground3 },
  dotStarted: { background: tokens.colorNeutralForeground3 },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  step: { fontWeight: 600, color: tokens.colorNeutralForeground1 },
  meta: { color: tokens.colorNeutralForeground3, fontSize: '12px', fontVariantNumeric: 'tabular-nums' },
  payload: {
    marginTop: '4px',
    padding: '8px 10px',
    background: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  err: { color: tokens.colorPaletteRedForeground1 },
  empty: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
});

function statusColor(status: string): 'success' | 'danger' | 'informative' {
  if (status === 'failed') return 'danger';
  if (status === 'started') return 'informative';
  return 'success';
}

function tryFormatPayload(raw: string): string {
  try {
    const obj = JSON.parse(raw);
    return JSON.stringify(obj, null, 2);
  } catch {
    return raw;
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const ms = d.getMilliseconds().toString().padStart(3, '0');
  return `${d.toLocaleTimeString()}.${ms}`;
}

export function PipelineTimeline({ runType, runId }: Props) {
  const s = useStyles();
  const [data, setData] = useState<Timeline | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr(null);
    setData(null);
    (async () => {
      try {
        const t = await apiGet<Timeline>(`/api/pipeline/${runType}/${runId}`);
        if (mounted) setData(t);
      } catch (e) {
        if (mounted) setErr((e as Error).message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [runType, runId]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (loading) return <Spinner size="tiny" label="Loading pipeline timeline…" />;
  if (err) return <Text className={s.err}>Timeline unavailable: {err}</Text>;
  if (!data || data.events.length === 0) {
    return <Text className={s.empty}>No pipeline events recorded for this run.</Text>;
  }

  return (
    <div>
      <div className={s.header}>
        <Text weight="semibold">
          {data.events.length} event{data.events.length === 1 ? '' : 's'} · total {data.totalDurationMs.toFixed(0)} ms
        </Text>
        <Button
          appearance="subtle"
          size="small"
          onClick={() => setExpanded(
            expanded.size === data.events.length
              ? new Set()
              : new Set(data.events.map((e: PipelineEvent) => e.id))
          )}
        >
          {expanded.size === data.events.length ? 'Collapse all' : 'Expand all'}
        </Button>
      </div>
      <div className={s.wrap}>
        {data.events.map((e: PipelineEvent) => {
          const isExpanded = expanded.has(e.id);
          const dotClass = e.status === 'failed'
            ? `${s.dot} ${s.dotFailed}`
            : e.status === 'started'
              ? `${s.dot} ${s.dotStarted}`
              : s.dot;
          return (
            <div key={e.id} className={s.row}>
              <span className={dotClass} />
              <div className={s.head}>
                <span className={s.step}>{e.step}</span>
                <Badge appearance="tint" color={statusColor(e.status)} size="small">{e.status}</Badge>
                {e.providerId && (
                  <Badge appearance="outline" size="small">{e.providerId}</Badge>
                )}
                {e.latencyMs != null && (
                  <span className={s.meta}>{e.latencyMs.toFixed(0)} ms</span>
                )}
                <span className={s.meta}>{formatTime(e.occurredAt)}</span>
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => toggle(e.id)}
                >
                  {isExpanded ? 'Hide payload' : 'Show payload'}
                </Button>
              </div>
              {isExpanded && (
                <pre className={s.payload}>{tryFormatPayload(e.payload)}</pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
